// Bajar VARIOS archivos de una — ADR-2026-09-08-descargar-todo-desde-el-chat.
//
// Dos formas, porque sirven para cosas distintas: un ZIP (el default: un solo archivo, sin
// pelearse con el navegador) o archivos sueltos (para quien los quiere directo en Descargas
// y los va a arrastrar a otro lado).
//
// La extensión sale de los BYTES, no del nombre en Storage: hay imágenes guardadas como
// `.png` que por dentro son JPEG, y una extensión que miente rompe la miniatura de Finder y
// a cualquier consumidor que valide por extensión (ADR-2026-09-08-descargas-con-extension-que-miente).
//
// El ZIP se arma en memoria: son decenas de imágenes, no gigas. Si alguna falla se saltea y
// se sigue — bajar 23 de 24 es mejor que no bajar nada— y al final se avisa cuántas
// quedaron afuera.

import { fixExtensionFromBlob } from './downloadFileName';
import { marcarDescargadas } from './marcarDescargada';

/** Un archivo por bajar. `url` ya tiene que estar firmada. */
export interface ArchivoDeLote {
  url: string;
  /** Nombre propuesto, con extensión. Se corrige por bytes antes de escribirlo. */
  nombre: string;
  /**
   * El id de `imagenes_generadas` cuando el archivo ES una creación: al bajarla se marca
   * `descargada` (`marcarDescargadas`). Sin esto no se marca nada — el material de
   * referencia (productos, fondos, modelos) no lleva id porque no es una creación.
   */
  imagenId?: string;
}

export interface ResultadoLote {
  /** Cuántos entraron. */
  ok: number;
  /** Cuántos no se pudieron bajar. */
  fallidos: number;
}

export type OnProgreso = (hechos: number, total: number) => void;

/**
 * Qué hacerle a cada archivo antes de escribirlo: marca de agua, formato de salida
 * (ADR-2026-09-14-configuracion-de-descarga). Se pregunta UNA vez por lote y se aplica
 * a todos; si no se pasa, el lote baja los originales como siempre.
 *
 * Corre ANTES de decidir el nombre: la extensión sale de los bytes, y los bytes son los
 * de la imagen ya convertida — un WebP no puede terminar llamándose `.png`.
 */
export type Transformar = (blob: Blob) => Promise<Blob>;

/** Pausa entre descargas sueltas: sin esto el navegador bloquea la ráfaga. */
const PAUSA_ENTRE_DESCARGAS_MS = 400;

function bajarBlob(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  // Diferido: revocar en el mismo tick cancela la descarga en algunos navegadores.
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 200);
}

async function traer(
  a: ArchivoDeLote,
  transformar?: Transformar,
): Promise<{ blob: Blob; nombre: string } | null> {
  try {
    const res = await fetch(a.url);
    if (!res.ok) return null;
    const crudo = await res.blob();
    if (crudo.size === 0) return null;
    const blob = transformar ? await transformar(crudo) : crudo;
    return { blob, nombre: await fixExtensionFromBlob(a.nombre, blob) };
  } catch {
    return null;
  }
}

/** Todo junto en un `.zip`. */
export async function descargarComoZip(
  archivos: ArchivoDeLote[],
  nombreZip: string,
  onProgreso?: OnProgreso,
  transformar?: Transformar,
): Promise<ResultadoLote> {
  const { zip } = await import('fflate');
  const contenido: Record<string, Uint8Array> = {};
  /** Las creaciones que entraron al zip: se marcan recién con el zip en Descargas. */
  const bajadas: string[] = [];
  let ok = 0;
  let hechos = 0;
  for (const a of archivos) {
    const bajado = await traer(a, transformar);
    if (bajado) {
      if (a.imagenId) bajadas.push(a.imagenId);
      // Dos nombres iguales dentro del zip se pisarían en silencio.
      let nombre = bajado.nombre;
      if (contenido[nombre]) {
        const punto = nombre.lastIndexOf('.');
        const base = punto >= 0 ? nombre.slice(0, punto) : nombre;
        const ext = punto >= 0 ? nombre.slice(punto) : '';
        nombre = `${base}-${ok + 1}${ext}`;
      }
      contenido[nombre] = new Uint8Array(await bajado.blob.arrayBuffer());
      ok += 1;
    }
    hechos += 1;
    onProgreso?.(hechos, archivos.length);
  }
  if (ok === 0) return { ok: 0, fallidos: archivos.length };
  const comprimido: Uint8Array = await new Promise((resolve, reject) => {
    zip(contenido, (err, data) => (err ? reject(err) : resolve(data)));
  });
  bajarBlob(new Blob([comprimido as unknown as BlobPart], { type: 'application/zip' }), nombreZip);
  if (bajadas.length > 0) void marcarDescargadas(bajadas);
  return { ok, fallidos: archivos.length - ok };
}

/** Uno por uno, como archivos sueltos en Descargas. */
export async function descargarSueltos(
  archivos: ArchivoDeLote[],
  onProgreso?: OnProgreso,
  transformar?: Transformar,
): Promise<ResultadoLote> {
  const bajadas: string[] = [];
  let ok = 0;
  let hechos = 0;
  for (const a of archivos) {
    const bajado = await traer(a, transformar);
    if (bajado) {
      bajarBlob(bajado.blob, bajado.nombre);
      if (a.imagenId) bajadas.push(a.imagenId);
      ok += 1;
      await new Promise((r) => setTimeout(r, PAUSA_ENTRE_DESCARGAS_MS));
    }
    hechos += 1;
    onProgreso?.(hechos, archivos.length);
  }
  // ⚠️ Techo conocido, y se deja a propósito (review Greptile #122): `bajarBlob` es un
  // click a un `<a download>` y el navegador no avisa si lo aceptó, así que Chrome podría
  // bloquear parte de una ráfaga y esas igual quedarían marcadas. No se cambia por tres
  // razones: no hay API para confirmar la entrega; `ok` —el número que la pantalla YA le
  // muestra a la persona ("Bajé 23; 1 falló")— se cuenta exactamente igual, así que dejar
  // de marcar haría que la base contradiga al cartel; y la ráfaga va espaciada 400 ms
  // justo para eso, además de que de tres archivos en adelante el criterio del repo es
  // zip y no sueltos. `descargada` es un proxy de engagement, no una contabilidad.
  if (bajadas.length > 0) void marcarDescargadas(bajadas);
  return { ok, fallidos: archivos.length - ok };
}
